import * as React from 'react';

import { cn, getTimezoneAbbreviation } from '@/lib/utils';

import WaitlistTimer from './assets/waitlist-timer.svg';
import Wreath from './wreath';

export interface ConfirmBatchCardProps {
  batchName: string;
  batchCode: string;
  scheduleDetails?: {
    day: string;
    time: string;
  }[];
  startDay?: string;
  startDate?: string;
  className?: string;
  email?: string;
  theme?: 'math' | 'english' | 'waitlist';
  timezone?: string;
  isStartingSoon?: boolean;
  isOngoing?: boolean;
  completedSessions?: number;
}

const ConfirmBatchCard = React.forwardRef<
  HTMLDivElement,
  ConfirmBatchCardProps
>(
  (
    {
      batchName,
      batchCode,
      scheduleDetails,
      startDay,
      startDate,
      className,
      theme = 'math',
      timezone,
      isStartingSoon = false,
      isOngoing = false,
      ...props
    },
    ref
  ) => {
    const timezoneAbbr = timezone
      ? getTimezoneAbbreviation(timezone)
      : undefined;
    const themeStyles = {
      math: {
        background: 'bg-blue-chaos-light-cardbg',
        border: 'border-blue-chaos-dark',
        divider: 'border-blue-chaos-dark',
        subtitle: 'text-magical-moonlight-d1',
        leafColor: 'var(--color-blue-chaos)',
        leafSecondaryColor: 'var(--color-blue-chaos-dark)',
      },
      english: {
        background: 'bg-cold-lips-light-cardbg',
        border: 'border-cold-lips-dark',
        divider: 'border-cold-lips-dark',
        subtitle: 'text-magical-moonlight-d1',
        leafColor: 'var(--color-cold-lips)',
        leafSecondaryColor: 'var(--color-blue-chaos-dark)',
      },
      waitlist: {
        background: 'bg-hot-sun-yellow-light-cardbg',
        border: 'border-hot-sun-dark',
        divider: 'border-hot-sun-dark',
        subtitle: 'text-hot-sun-dark',
        leafColor: 'var(--color-warning-yellow)',
        leafSecondaryColor: 'var(--color-hot-sun-dark)',
      },
    };

    const currentTheme = themeStyles[theme];
    return (
      <div
        ref={ref}
        className={cn(
          `${currentTheme.background} relative p-0 rounded-3xl border my-2 border-dashed ${currentTheme.border} md:w-140 w-85 max-w-[calc(100%-3rem)] mx-auto`,
          className
        )}
        {...props}
      >
        {theme === 'waitlist' && (
          <img
            src={WaitlistTimer}
            alt="waitlist timer"
            className="absolute -top-2.5 right-4"
          />
        )}
        {theme !== 'waitlist' && isStartingSoon ? (
          <div className="absolute -top-2.5 right-4 text-magical-moonlight-white text-xs font-medium px-2 py-0.5 rounded bg-cold-lips-dark">
            Yet to start
          </div>
        ) : null}
        {theme !== 'waitlist' && isOngoing ? (
          <div className="absolute -top-2.5 right-4 text-magical-moonlight-white text-xs font-medium px-2 py-0.5 rounded bg-blue-chaos-dark!">
            Ongoing
          </div>
        ) : null}
        <div className="flex flex-col items-center relative size-full">
          <div className="box-border content-stretch flex flex-col gap-1 items-center justify-start px-2 py-3 relative size-full">
            <div className="box-border flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
              <Wreath
                primaryColor={currentTheme.leafColor}
                secondaryColor={currentTheme.leafSecondaryColor}
                className="mr-2"
              />
              <div className="flex flex-col items-center justify-center text-center">
                <h2 className="font-bold text-magical-moonlight-d4 text-sm md:text-base leading-6 whitespace-nowrap">
                  {batchName}
                </h2>
                <span
                  className={`font-medium ${currentTheme.subtitle} text-xs md:text-sm leading-6 whitespace-nowrap`}
                >
                  {batchCode}
                </span>
              </div>
              <Wreath
                className="scale-x-[-1] ml-2"
                primaryColor={currentTheme.leafColor}
                secondaryColor={currentTheme.leafSecondaryColor}
              />
            </div>
            {theme === 'waitlist' ? (
              <div className="flex flex-col items-center">
                <div className="text-magical-moonlight-d1 text-xs md:text-sm">
                  {`Our team will reach out to you shortly`}
                </div>
                {/* <div className="text-magical-moonlight-d1 text-xs md:text-sm">
                  {`You'll be notified by email on `}
                  <span className="text-magical-moonlight-d4 text-xs md:text-sm">
                    {email}
                  </span>
                </div>
                <div className="bg-magical-moonlight-white rounded-lg w-full mt-4 px-2 py-2 flex items-center gap-2">
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-3 md:w-4 h-3 md:h-4 text-hot-sun"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="font-medium text-magical-moonlight-d3 text-[10px] md:text-xs">
                    {`More batches coming soon—we'll keep you posted!`}
                  </span>
                </div> */}
              </div>
            ) : (
              <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
                <div className="flex flex-row gap-2 items-start w-40 md:w-56">
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-3 md:w-4 h-3 md:h-4 text-hot-sun shrink-0 mt-0.5"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="font-medium text-magical-moonlight-d1 text-xs md:text-sm leading-6 whitespace-nowrap">
                      Sessions will be on every
                    </span>
                    <div className="flex flex-row space-x-2 md:space-x-5 mt-1 md:mt-0">
                      {scheduleDetails?.map((schedule, index) => (
                        <div
                          key={index}
                          className="font-semibold text-xs md:text-sm w-14"
                        >
                          <span className="text-magical-moonlight-d3 leading-4 md:leading-5 block text-[10px] md:text-xs">
                            {schedule.day}
                          </span>
                          <span className="text-cold-lips-dark leading-4 md:leading-5 block text-[10px] md:text-xs">
                            {schedule.time}
                            {timezoneAbbr ? (
                              <span className="text-[10px] md:text-xs font-semibold text-cold-lips-dark ml-1">
                                {timezoneAbbr}
                              </span>
                            ) : null}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div
                  className={`h-20 w-px border-l border-dashed ${currentTheme.divider} mx-3 md:mx-5`}
                />
                <div className="flex flex-row gap-2 items-start w-34 md:w-56">
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-3 md:w-4 h-3 md:h-4 text-hot-sun shrink-0 mt-0.5"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="font-medium text-magical-moonlight-d1 text-xs md:text-sm leading-6 whitespace-nowrap">
                      {isOngoing ? 'Started on' : 'Starts from'}
                    </span>
                    <span className="font-semibold text-magical-moonlight-d3 text-xs md:text-sm leading-4 md:leading-6">
                      {startDay}
                    </span>
                    <span className="font-semibold text-cold-lips-dark text-xs md:text-sm leading-4 md:leading-6">
                      {startDate}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ConfirmBatchCard.displayName = 'ConfirmBatchCard';

export { ConfirmBatchCard };
export default ConfirmBatchCard;
